import {
  FormArray,
  FormArrayName,
  FormControl,
  FormGroup,
  FormGroupName,
  NgControl,
} from '@angular/forms';
import { inject, Type } from '@angular/core';

export function getAllFormErrors(form: FormGroup | FormArray, fieldPath = '') {
  let errors: { control: string; errorKey: string; error: any }[] = [];

  Object.keys(form.controls).forEach((key) => {
    const control = form.get(key);
    const newPath = fieldPath ? `${fieldPath}.${key}` : key;

    if (control instanceof FormGroup || control instanceof FormArray) {
      errors = errors.concat(getAllFormErrors(control, newPath));
    } else if (control?.errors) {
      Object.keys(control.errors).forEach((errorKey) => {
        errors.push({
          control: newPath,
          errorKey,
          error: control.errors?.[errorKey],
        });
      });
    }
  });

  return errors;
}

export function clearAllFormErrors(form: FormGroup | FormArray) {
  Object.keys(form.controls).forEach((key) => {
    const control = form.get(key);
    if (control instanceof FormGroup || control instanceof FormArray) {
      clearAllFormErrors(control);
    } else {
      control?.setErrors(null);
      control?.markAsPristine();
      control?.markAsUntouched();
    }
  });
}

export function injectFormElements(
  require?: Type<FormControl | FormGroup | FormArray>,
) {
  const formGroup = inject(FormGroup, {
    optional: true,
    self: true,
  });
  const formGroupName = inject(FormGroupName, {
    optional: true,
    self: true,
  });
  const formArray = inject(FormArray, {
    optional: true,
    self: true,
  });
  const formArrayName = inject(FormArrayName, {
    optional: true,
    self: true,
  });
  const ngControl = inject(NgControl, {
    optional: true,
    self: true,
  });
  return { formGroup, formGroupName, formArray, formArrayName, ngControl };
}

export type FormElement = FormControl | FormGroup | FormArray;
export type FormElementInjects = {
  formGroup: FormGroup | null;
  formGroupName: FormGroupName | null;
  formArray: FormArray | null;
  formArrayName: FormArrayName | null;
  ngControl: NgControl | null;
};

export function retrieveFormElement<T extends FormElement>(
  injects: FormElementInjects,
  instanceOf?: Type<T>,
): T | undefined {
  const control =
    (injects.ngControl ?? injects.formGroupName ?? injects.formArrayName)
      ?.control ??
    injects.formGroup ??
    injects.formArray;
  if (control) {
    if (instanceOf && !(control instanceof instanceOf))
      throw new Error(
        `Injected form element should be ${instanceOf}. Received ${control} instead.`,
      );
  }
  return control as T;
}
